import { ref, watch, computed } from '@vue/composition-api';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import Company from '@/models/Company';
import { TABLE, COMPANY } from '@/constants/config';
import { pluck, formatDate } from '@/helpers';

export default function useCompaniesTable() {
    const toast = useToast();

    const refCompanyListTable = ref(null);
    const companies = ref(null);

    // Table Handlers
    const tableColumns = [
        { key: 'index', label: '#', thClass: 'px-1 pr-2', tdClass: 'px-1 pr-2' },
        { key: 'uen', label: 'UEN', thClass: 'px-0 pr-2', tdClass: 'px-0 pr-2' },
        { key: 'companyName', label: 'COMPANY', sortable: true, thClass: 'px-1 text-nowrap', tdClass: 'px-1' },
        {
            key: 'financialYearEnd',
            label: 'FYE',
            sortable: true,
            thClass: 'px-1 text-nowrap',
            tdClass: 'px-1 text-nowrap',
            formatter: value => formatDate(value)
        },
        { 
            key: 'lastAR',
            label: 'Last AR',
            sortable: true,
            thClass: 'px-1 text-nowrap',
            tdClass: 'px-1 text-nowrap',
            formatter: value => formatDate(value)
        },
        { 
            key: 'fyeOfLastAgm',
            label: 'FYE of Last AGM',
            sortable: true ,
            thClass: 'px-1',
            tdClass: 'px-1 text-nowrap',
            formatter: value => formatDate(value)
        },
        { 
            key: 'agm',
            label: 'AGM',
            sortable: true,
            thClass: 'px-1 text-nowrap',
            tdClass: 'px-1 text-nowrap',
            formatter: value => formatDate(value)
        },
        { key: 'status', label: 'STATUS', sortable: true, thClass: 'px-1 text-nowrap', tdClass: 'px-1 text-nowrap' },
        { key: 'actions', label: 'ACTION', thClass: 'pl-0 pr-1 text-right', tdClass: 'px-0 pr-1 text-right' },
    ];

    const perPage = ref(TABLE.PER_PAGE);
    const totalCompanies = ref(0);
    const currentPage = ref(1);
    const perPageOptions = TABLE.PER_PAGE_OPTIONS;
    const searchQuery = ref('');
    const sortBy = ref('id');
    const isSortDirDesc = ref(true);
    const isBusy = ref(false);

    // local variable to store the companies list
    const localCompanies = ref([]);
    const companyStatus = ref([]);

    const isLoading = ref(false);

    // options where the search query is applied
    // make sure to list as keys of how to access those values in table
    const filterOn = ref([
        'uen',
        'fullCompanyName',
        'companyDomain.text',
    ]);

    const dataMeta = computed(() => {
        const localItemsCount = refCompanyListTable.value ? refCompanyListTable.value.localItems.length : 0;
        totalCompanies.value = localItemsCount;
        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: totalCompanies.value,
        };
    });

    // Call FormatDate method before showing info in table, to be able to search by formatted date...
    const localizeData = data => ({
        id: data?.id,
        uen: data?.company_information?.uen,
        companyName: data?.name,
        fullCompanyName: data?.full_company_name,
        companyDomain: data?.company_type ?
            {
                value:data.company_type.id,
                text:data.company_type.name
            }
            : undefined,
        financialYearEnd: formatDate(data?.year_end),
        lastAR: formatDate(data?.company_information?.last_AR),
        fyeOfLastAgm: formatDate(data?.company_information?.FYE_of_last_AGM),
        incorporationDate: formatDate(data?.company_information?.incorporation_date),
        agm: formatDate(data?.company_information?.AGM),
        status: {
            text: data.status,
            value: data.status
        },
    });

    const fetchCompanies = () => {
        isLoading.value = true;

        Company
            .getIncorporatedCompanies()
            .then(response => {
                localCompanies.value = response.data.data.map(cp => localizeData(cp));

                // eslint-disable-next-line no-param-reassign
                companies.value = localCompanies.value;

                isLoading.value = false;
            })
            .catch(e => {
                console.error("Error fetching companies", e);

                toast({
                    component: ToastificationContent,
                    props: {
                        title: 'Error fetching company list',
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                    },
                });
            });
    };

    fetchCompanies();

    const refetchData = () => {
        refCompanyListTable.value.refresh();
    };

    watch([currentPage, perPage, searchQuery], () => {
        refetchData();
        isBusy.value = true;
        let data = localCompanies.value;
        const queryLowered = searchQuery.value.toLowerCase().trim();

        //  if a query present then search
        if (queryLowered)
        {
            data = localCompanies.value.filter(item => filterOn.value.some(key => {
                const value = pluck(key, item);
                return value && value.toString().toLowerCase().trim().includes(queryLowered);
            }));
        }

        refCompanyListTable.value.localItems = data;
        isBusy.value = false;
    });

    const resolveCompanyStatusVariant = status => {
        if (status === COMPANY.STATUS_INCORPORATED) return 'success';
        if (status === COMPANY.STATUS_ACTIVE) return 'success';
        if (status === COMPANY.STATUS_PENDING) return 'warning';
        if (status === COMPANY.STATUS_IN_PROGRESS) return 'warning';
        if (status === COMPANY.STATUS_INACTIVE) return 'secondary';
        return 'primary';
    };

    return {
        sortBy,
        isBusy,
        perPage,
        dataMeta,
        isLoading,
        searchQuery,
        currentPage,
        tableColumns,
        isSortDirDesc,
        totalCompanies,
        perPageOptions,
        refCompanyListTable,

        filterOn,
        refetchData,
        localizeData,
        // fetchCompanies,
        resolveCompanyStatusVariant,
        companyStatus,

        // Api data
        companies,
    };
}
